// Libraries
import {
    memo, useContext, useEffect, useMemo, 
    useState
} from 'react';
import {
    useTheme 
} from '@mui/material';

// Utils
import {
    createGoalsSkip, createPhoneSkip
} from '../../../config/utils';

// Config
import {
    defaultSignupSections,
    DiagnosticFormContext 
} from '../../../../../contexts/form';
import {
    ZumbaChallengeTitle 
} from './graphics';
import {
    ZUMBA_CHALLENGES 
} from '../../../config/types';
import {
    useCookies 
} from 'react-cookie';

// Components
import SCREENS from '../sections';

// opt in flow for zumba challenge
// v2 of new diagnostic
// removed intro animation
// updated drink baselines screen
// joint information capture
// removed phone permission screen
// shortened the plan section
// joint premium checkout

type Props = {
    index: number;
};

export default memo(function DiagnosticForm20250220({
    index = 0 
}: Props) {
    const diagnosticFormContext = useContext(DiagnosticFormContext);
    const theme = useTheme();
    const [cookies] = useCookies();
    const [isLoading, setIsLoading] = useState(true);
    const skippySkipsGoals = useMemo(() => createGoalsSkip(diagnosticFormContext, 33), [diagnosticFormContext]);
    const skippySkipsPhone = useMemo(() => createPhoneSkip(diagnosticFormContext, 11), [diagnosticFormContext]);

    // Set initial values and functions in context
    useEffect(() => {
        diagnosticFormContext.skippySkipsGoals = skippySkipsGoals;
        diagnosticFormContext.skippySkipsPhone = skippySkipsPhone;
        diagnosticFormContext.sections = [
            {
                key: 'challenge',
                headerText: 'Set Up Your Challenge',
                length: 1,
                colors: {
                    background: theme.palette.zumba['100'],
                    main: theme.palette.zumba.main,
                    headerText: theme.palette.zumba.dark,
                    choiceText: theme.palette.zumba.dark,
                    choice: theme.palette.zumba.light,
                    ctaBackground: theme.palette.transparent,
                    ctaButton: theme.palette.zumba.main,
                    ctaButtonText: theme.palette.white,
                    ctaText: theme.palette.neutral.main,
                    buttonArrow: theme.palette.white,
                    circleTakeover: theme.palette.zumba['100'],
                    trophy: theme.palette.zumba.main
                },
                icon: <ZumbaChallengeTitle />,
                inProgressBar: false,
                challengeName: 'Zumba Drink Less Dance More',
                challengeDescription: 'In honor of Alcohol Awareness Month, Sunnyside and Zumba® — the most popular dance fitness program in the world — are teaming up to help you swap sips for salsa. Join this challenge for an extra boost to reach your healthy drinking goals, and get active!',
                challengeOptions: ZUMBA_CHALLENGES,
                challengeIdentifier: 'zumba-2025-04',
                hasTrophy: true
            },
            ...defaultSignupSections
        ];

        const checkoutSection = diagnosticFormContext.sections.find(s => s.key === 'checkout');
        checkoutSection.colors = {
            ...checkoutSection.colors,
            ctaBackground: theme.palette.success.light,
            ctaButton: theme.palette.success.main,
        };
        diagnosticFormContext.sections[0].length = 7;
        diagnosticFormContext.sections[1].length = 18;
        diagnosticFormContext.sections[2].length = 5;
        diagnosticFormContext.sections[3].length = 22;
        setIsLoading(false);
    }, []);

    useEffect(() => {
        const trackingName = screens[index].trackingName;
        const screenName = screens[index].screenName;

        if (trackingName) diagnosticFormContext.fireTrackingEvents(diagnosticFormContext, trackingName, cookies);
        if (screenName) diagnosticFormContext.currentScreenName = screenName;
    }, [index]);

    const screens = useMemo(() => ([
        ...SCREENS.CHALLENGE_SCREENS,
        ...SCREENS.GOALS_SCREENS,
        ...SCREENS.HABITS_SCREENS_V4,
        ...SCREENS.ABOUT_YOU_SCREENS_V2,
        ...SCREENS.PLAN_SCREENS_V3,
        ...SCREENS.CHECKOUT_SCREENS_V3
    ]), []);

    if (isLoading) return <></>;

    return (
        <>
            {screens[index].component}
        </>
    );
});
